import { useNavigate } from 'react-router';
import type { TestProcedureRow } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { Pagination } from '#client/components/ui/Pagination';
import { SearchFilter } from '#client/components/ui/SearchFilter';
import { useCan } from '#client/hooks/useCan';
import { useResourceList } from '#client/hooks/useResourceList';
import { RESOURCE, routes } from '#client/lib/routes';
import { procedureColumns } from './procedure-columns';
import { ButtonLink } from '#client/components/ui/ButtonLink';

/** The SOP library — what the lab currently keeps as Word documents. */
export function TestProcedureListPage() {
  const navigate = useNavigate();
  const can = useCan();
  const procedures = useResourceList<TestProcedureRow>({ path: RESOURCE.testProcedures });

  return (
    <Page
      title="Test procedures"
      subtitle="How each test is run — method, specimen, steps and what it consumes"
      primaryAction={
        can('procedure.create') ? (
          <ButtonLink variant="primary" size="md" to={routes.testProcedureNew}>
            New procedure
          </ButtonLink>
        ) : undefined
      }
    >
      <Card flush>
        <div className="c-import-filter">
          <SearchFilter
            value={procedures.search}
            onChange={procedures.setSearch}
            placeholder="Title"
          />
        </div>

        <DataTable
          columns={procedureColumns}
          rows={procedures.items}
          rowKey={(row) => row.id}
          loading={procedures.loading}
          skeletonRows={procedures.limit}
          caption="Test procedures"
          onRowClick={(row) => void navigate(routes.testProcedure(row.id))}
          emptyState={
            <EmptyState
              title="No procedures yet"
              body="Write up the SOPs the bench already follows, so the reagent volumes and the stock ledger agree."
            />
          }
        />

        <Pagination
          page={procedures.page}
          limit={procedures.limit}
          count={procedures.count}
          onPageChange={procedures.setPage}
        />
      </Card>
    </Page>
  );
}
